import { useState, useEffect, useRef, useMemo } from "react";

const STATUS_OPTIONS = ["New", "Contacted", "In Progress", "Closed"];

const STATUS_COLORS = {
  "New":         { bg: "#EFF6FF", text: "#1D4ED8", dot: "#3B82F6" },
  "Contacted":   { bg: "#FFF7ED", text: "#C2410C", dot: "#F97316" },
  "In Progress": { bg: "#F0FDF4", text: "#15803D", dot: "#22C55E" },
  "Closed":      { bg: "#F1F5F9", text: "#475569", dot: "#94A3B8" },
};

const STAT_ACCENTS = {
  "New":         "#3B82F6",
  "Contacted":   "#F97316",
  "In Progress": "#22C55E",
  "Closed":      "#94A3B8",
};

export default function AdminReferrals() {
  const [referrals, setReferrals]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selected, setSelected]     = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch]         = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const drawerRef = useRef(null);

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => { fetchReferrals(); }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") closeDrawer(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/referrals`);
      const data = await res.json();
      setReferrals(data);
    } catch (err) {
      setError("Failed to load referrals.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return referrals.filter((r) => {
      const matchesStatus = filterStatus === "All" || r.status === filterStatus;
      const matchesSearch =
        !q ||
        r.referredName?.toLowerCase().includes(q) ||
        r.referredEmail?.toLowerCase().includes(q) ||
        r.referredPhone?.toLowerCase().includes(q) ||
        r.yourName?.toLowerCase().includes(q) ||
        r.notes?.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [referrals, search, filterStatus]);

  const openDrawer = (r) => { setSelected(r); setDrawerOpen(true); };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelected(null), 280);
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/referrals/${id}/status`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status }),
      });
      const updated = await res.json();
      setReferrals((prev) => prev.map((r) => r._id === id ? updated : r));
      if (selected?._id === id) setSelected(updated);
    } catch (err) {
      alert("Failed to update status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this referral?")) return;
    try {
      await fetch(`${API}/api/referrals/${id}`, { method: "DELETE" });
      setReferrals((prev) => prev.filter((r) => r._id !== id));
      closeDrawer();
    } catch (err) {
      alert("Failed to delete referral.");
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&display=swap');

        .ar-root {
          padding: 40px 48px;
          background: #F7F4EF;
          min-height: 100vh;
          font-family: Constantia, 'Book Antiqua', Georgia, serif;
          box-sizing: border-box;
        }
        .ar-eyebrow {
          font-family: 'Cinzel', serif;
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #012E72;
          opacity: 0.45;
          margin: 0 0 6px;
        }
        .ar-title {
          font-family: 'Cinzel', serif;
          font-size: 30px;
          font-weight: 700;
          color: #012E72;
          margin: 0;
          line-height: 1.1;
        }
        .ar-subtitle {
          margin: 6px 0 0;
          font-size: 15px;
          color: #94A3B8;
        }
        .ar-rule {
          border: none;
          border-top: 1px solid #E2DDD6;
          margin: 24px 0 32px;
        }
        .ar-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 36px;
        }
        .ar-stat {
          background: #fff;
          border-radius: 14px;
          padding: 20px 22px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 1px 3px rgba(1,46,114,0.06);
          position: relative;
          overflow: hidden;
          cursor: pointer;
          transition: box-shadow 0.15s;
        }
        .ar-stat:hover { box-shadow: 0 3px 10px rgba(1,46,114,0.1); }
        .ar-stat.active { box-shadow: 0 3px 10px rgba(1,46,114,0.12); }
        .ar-stat::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 4px;
          border-radius: 14px 0 0 14px;
          background: var(--accent);
        }
        .ar-stat-num {
          font-family: 'Cinzel', serif;
          font-size: 28px;
          font-weight: 700;
          color: #012E72;
          line-height: 1;
        }
        .ar-stat-label {
          font-size: 12px;
          color: #94A3B8;
          margin-top: 3px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-family: 'Cinzel', serif;
        }

        /* ── Toolbar ── */
        .ar-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .ar-search-wrap {
          position: relative;
          flex: 1;
          min-width: 200px;
        }
        .ar-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94A3B8;
          pointer-events: none;
          display: flex;
        }
        .ar-search {
          width: 100%;
          padding: 9px 12px 9px 36px;
          border: 1.5px solid #E2DDD6;
          border-radius: 10px;
          font-family: Constantia, 'Book Antiqua', Georgia, serif;
          font-size: 14px;
          color: #012E72;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
        }
        .ar-search::placeholder { color: #94A3B8; }
        .ar-search:focus { border-color: #012E72; }
        .ar-search-clear {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #94A3B8;
          display: flex;
          padding: 2px;
          border-radius: 4px;
          transition: color 0.15s;
        }
        .ar-search-clear:hover { color: #012E72; }
        .ar-filter-tabs {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .ar-filter-tab {
          padding: 8px 14px;
          border-radius: 10px;
          font-family: 'Cinzel', serif;
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-weight: 600;
          cursor: pointer;
          border: 1.5px solid transparent;
          transition: all 0.15s;
          background: #fff;
          color: #94A3B8;
          border-color: #E2DDD6;
        }
        .ar-filter-tab:hover { color: #012E72; border-color: #012E72; }
        .ar-filter-tab.active {
          background: #012E72;
          color: #fff;
          border-color: #012E72;
        }

        .ar-results-count {
          font-size: 12px;
          color: #94A3B8;
          margin-bottom: 12px;
          font-family: 'Cinzel', serif;
          letter-spacing: 0.05em;
        }

        /* ── Table ── */
        .ar-table-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(1,46,114,0.07);
          overflow: hidden;
        }
        .ar-table-header-row {
          display: grid;
          grid-template-columns: 1fr 1fr 120px 110px 44px;
          padding: 12px 24px;
          border-bottom: 1px solid #F1EDE7;
          background: #FDFCFB;
        }
        .ar-th {
          font-family: 'Cinzel', serif;
          font-size: 9.5px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #012E72;
          opacity: 0.4;
          font-weight: 600;
        }
        .ar-row {
          display: grid;
          grid-template-columns: 1fr 1fr 120px 110px 44px;
          padding: 16px 24px;
          border-bottom: 1px solid #F7F4EF;
          cursor: pointer;
          transition: background 0.15s;
          align-items: center;
        }
        .ar-row:last-child { border-bottom: none; }
        .ar-row:hover { background: #FDFCFB; }
        .ar-name {
          font-family: 'Cinzel', serif;
          font-size: 13.5px;
          font-weight: 600;
          color: #012E72;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding-right: 12px;
        }
        .ar-referrer { font-size: 11px; color: #94A3B8; margin-top: 2px; }
        .ar-contact {
          font-size: 13px;
          color: #64748B;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding-right: 12px;
        }
        .ar-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px 4px 8px;
          border-radius: 20px;
          font-family: 'Cinzel', serif;
          font-size: 9.5px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-weight: 600;
          width: fit-content;
        }
        .ar-badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .ar-date { font-size: 12.5px; color: #94A3B8; }
        .ar-action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          color: #CBD5E1;
          border-radius: 8px;
          transition: color 0.15s, background 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ar-action-btn:hover { color: #012E72; background: #EFF6FF; }

        /* ── Drawer ── */
        .ar-overlay {
          position: fixed;
          inset: 0;
          background: rgba(1, 20, 50, 0.25);
          backdrop-filter: blur(2px);
          z-index: 40;
          opacity: 0;
          transition: opacity 0.25s;
          pointer-events: none;
        }
        .ar-overlay.open { opacity: 1; pointer-events: all; }
        .ar-drawer {
          position: fixed;
          top: 0; right: 0; bottom: 0;
          width: 380px;
          background: #fff;
          z-index: 50;
          transform: translateX(100%);
          transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          box-shadow: -8px 0 40px rgba(1,46,114,0.1);
        }
        .ar-drawer.open { transform: translateX(0); }
        .ar-drawer-top {
          padding: 28px 28px 20px;
          border-bottom: 1px solid #F1EDE7;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        .ar-drawer-name {
          font-family: 'Cinzel', serif;
          font-size: 17px;
          font-weight: 700;
          color: #012E72;
          line-height: 1.25;
        }
        .ar-drawer-date { font-size: 12px; color: #94A3B8; margin-top: 4px; }
        .ar-drawer-close {
          background: #F7F4EF;
          border: none;
          border-radius: 8px;
          padding: 7px;
          cursor: pointer;
          color: #94A3B8;
          transition: color 0.15s, background 0.15s;
          flex-shrink: 0;
          display: flex;
        }
        .ar-drawer-close:hover { background: #EFF6FF; color: #012E72; }
        .ar-drawer-body {
          flex: 1;
          overflow-y: auto;
          padding: 24px 28px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .ar-field-label {
          font-family: 'Cinzel', serif;
          font-size: 9.5px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #012E72;
          opacity: 0.4;
          margin-bottom: 4px;
        }
        .ar-field-value { font-size: 14.5px; color: #012E72; }
        .ar-divider { border: none; border-top: 1px solid #F1EDE7; margin: 4px 0; }
        .ar-status-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .ar-status-btn {
          padding: 9px 8px;
          border-radius: 10px;
          font-family: 'Cinzel', serif;
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.15s;
          border: 1.5px solid transparent;
          font-weight: 600;
        }
        .ar-drawer-footer { padding: 20px 28px; border-top: 1px solid #F1EDE7; }
        .ar-delete-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: 1.5px solid #FCA5A5;
          color: #EF4444;
          border-radius: 10px;
          padding: 9px 16px;
          font-family: 'Cinzel', serif;
          font-size: 10.5px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.15s;
          width: 100%;
          justify-content: center;
          font-weight: 600;
        }
        .ar-delete-btn:hover { background: #FEF2F2; }
        .ar-empty { padding: 64px 24px; text-align: center; color: #94A3B8; font-size: 14px; }

        @media (max-width: 900px) {
          .ar-root { padding: 24px 20px; }
          .ar-stats { grid-template-columns: repeat(2, 1fr); }
          .ar-table-header-row,
          .ar-row { grid-template-columns: 1fr 120px 44px; }
          .ar-th:nth-child(2), .ar-row > *:nth-child(2) { display: none; }
          .ar-th:nth-child(4), .ar-row > *:nth-child(4) { display: none; }
          .ar-drawer { width: 100%; }
        }
      `}</style>

      <div className="ar-root">
        <p className="ar-eyebrow">Admin</p>
        <h1 className="ar-title">Referrals</h1>
        <p className="ar-subtitle">Manage and track all submitted referrals.</p>
        <hr className="ar-rule" />

        {/* Stat Cards — clickable to filter by status */}
        <div className="ar-stats">
          {STATUS_OPTIONS.map((status) => (
            <div
              key={status}
              className={`ar-stat${filterStatus === status ? " active" : ""}`}
              style={{
                "--accent": STAT_ACCENTS[status],
                outline: filterStatus === status ? `2px solid ${STAT_ACCENTS[status]}` : "none",
                outlineOffset: "2px",
              }}
              onClick={() => setFilterStatus(filterStatus === status ? "All" : status)}
            >
              <div>
                <div className="ar-stat-num">
                  {referrals.filter((r) => r.status === status).length}
                </div>
                <div className="ar-stat-label">{status}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="ar-toolbar">
          <div className="ar-search-wrap">
            <span className="ar-search-icon">
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
            </span>
            <input
              className="ar-search"
              type="text"
              placeholder="Search by name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="ar-search-clear" onClick={() => setSearch("")}>
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="ar-filter-tabs">
            {["All", ...STATUS_OPTIONS].map((s) => (
              <button
                key={s}
                className={`ar-filter-tab${filterStatus === s ? " active" : ""}`}
                onClick={() => setFilterStatus(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="ar-empty">Loading referrals…</div>
        ) : error ? (
          <div className="ar-empty" style={{ color: "#EF4444" }}>{error}</div>
        ) : (
          <>
            {(search || filterStatus !== "All") && (
              <p className="ar-results-count">
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                {filterStatus !== "All" ? ` · ${filterStatus}` : ""}
                {search ? ` · "${search}"` : ""}
              </p>
            )}
            {filtered.length === 0 ? (
              <div className="ar-empty">No referrals match your search.</div>
            ) : (
              <div className="ar-table-card">
                <div className="ar-table-header-row">
                  <span className="ar-th">Referred</span>
                  <span className="ar-th">Contact</span>
                  <span className="ar-th">Status</span>
                  <span className="ar-th">Date</span>
                  <span className="ar-th" />
                </div>
                {filtered.map((r) => (
                  <div key={r._id} className="ar-row" onClick={() => openDrawer(r)}>
                    <div>
                      <div className="ar-name">{r.referredName}</div>
                      {r.yourName && <div className="ar-referrer">via {r.yourName}</div>}
                    </div>
                    <div className="ar-contact">{r.referredEmail}</div>
                    <div>
                      <span
                        className="ar-badge"
                        style={{
                          backgroundColor: STATUS_COLORS[r.status]?.bg,
                          color: STATUS_COLORS[r.status]?.text,
                        }}
                      >
                        <span className="ar-badge-dot" style={{ backgroundColor: STATUS_COLORS[r.status]?.dot }} />
                        {r.status}
                      </span>
                    </div>
                    <div className="ar-date">
                      {new Date(r.createdAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </div>
                    <button
                      className="ar-action-btn"
                      onClick={(e) => { e.stopPropagation(); openDrawer(r); }}
                    >
                      <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className={`ar-overlay${drawerOpen ? " open" : ""}`} onClick={closeDrawer} />

      <div ref={drawerRef} className={`ar-drawer${drawerOpen ? " open" : ""}`} role="dialog" aria-modal="true">
        {selected && (
          <>
            <div className="ar-drawer-top">
              <div>
                <div className="ar-drawer-name">{selected.referredName}</div>
                <div className="ar-drawer-date">
                  Submitted {new Date(selected.createdAt).toLocaleDateString("en-US", {
                    year: "numeric", month: "long", day: "numeric",
                  })}
                </div>
              </div>
              <button className="ar-drawer-close" onClick={closeDrawer}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="ar-drawer-body">
              <div>
                <p className="ar-field-label">Email</p>
                <p className="ar-field-value">{selected.referredEmail}</p>
              </div>
              <div>
                <p className="ar-field-label">Phone</p>
                <p className="ar-field-value">{selected.referredPhone}</p>
              </div>
              {(selected.yourName || selected.yourEmail) && (
                <>
                  <hr className="ar-divider" />
                  {selected.yourName && (
                    <div>
                      <p className="ar-field-label">Referred By</p>
                      <p className="ar-field-value">{selected.yourName}</p>
                    </div>
                  )}
                  {selected.yourEmail && (
                    <div>
                      <p className="ar-field-label">Referrer Email</p>
                      <p className="ar-field-value">{selected.yourEmail}</p>
                    </div>
                  )}
                </>
              )}
              {selected.notes && (
                <>
                  <hr className="ar-divider" />
                  <div>
                    <p className="ar-field-label">Notes / Coverage Interest</p>
                    <p className="ar-field-value" style={{ lineHeight: 1.6 }}>{selected.notes}</p>
                  </div>
                </>
              )}
              <hr className="ar-divider" />
              <div>
                <p className="ar-field-label" style={{ marginBottom: 10 }}>Update Status</p>
                <div className="ar-status-grid">
                  {STATUS_OPTIONS.map((status) => {
                    const active = selected.status === status;
                    return (
                      <button
                        key={status}
                        className="ar-status-btn"
                        onClick={() => handleStatusChange(selected._id, status)}
                        style={{
                          backgroundColor: active ? STATUS_COLORS[status]?.bg : "#F7F4EF",
                          color: active ? STATUS_COLORS[status]?.text : "#94A3B8",
                          borderColor: active ? STATUS_COLORS[status]?.text : "transparent",
                        }}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="ar-drawer-footer">
              <button className="ar-delete-btn" onClick={() => handleDelete(selected._id)}>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Referral
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}